import React, { useState, useMemo } from 'react';
import { useBudgets } from '../contexts/BudgetContext';
import { useTransactions } from '../contexts/TransactionContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { calculateBudgetIntelligence, BUDGET_STATUS } from 'shared/utils/budgetIntelligence';
import {
  Bell,
  AlertTriangle,
  AlertOctagon,
  Flame,
  CheckCircle2,
  X,
  ChevronRight,
  Sparkles,
  SlidersHorizontal,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

export default function NotificationBell({ onOpenBudgetSettings }) {
  const { budgets } = useBudgets();
  const { allTransactions, selectedMonth } = useTransactions();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo ? getCurrencyInfo(displayCurrency) : null;
  const currencySymbol = currencyInfo?.symbol || '$';
  const { theme, isDark } = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Compute smart budget intelligence using user's real currency formatter
  const budgetData = useMemo(() => {
    return calculateBudgetIntelligence(budgets, allTransactions, selectedMonth, formatCurrency);
  }, [budgets, allTransactions, selectedMonth, formatCurrency]);

  const activeAlerts = budgetData?.activeAlerts || [];
  const alertCount = activeAlerts.length;
  const criticalCount = activeAlerts.filter((a) => a.status === BUDGET_STATUS.OVERBUDGET).length;
  const warningCount = activeAlerts.filter((a) => a.status === BUDGET_STATUS.WARNING).length;

  const showBadge = alertCount > 0 && !isDismissed;

  return (
    <>
      {/* Bell Trigger Button */}
      <button
        type="button"
        onClick={() => {
          setIsDismissed(false);
          setIsOpen(true);
        }}
        className="relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none"
        style={{
          backgroundColor: isOpen
            ? isDark ? '#374151' : '#E5E7EB'
            : isDark ? theme.inputBackground : '#F3F4F6',
          border: `1px solid ${theme.divider}`,
        }}
        title={alertCount > 0 ? `${alertCount} budget warnings detected` : 'No budget warnings'}
        data-testid="notification-bell-btn"
      >
        <Bell
          className="w-5 h-5 transition-colors"
          style={{
            color: showBadge
              ? criticalCount > 0
                ? '#EF4444'
                : '#F59E0B'
              : theme.textSecondary,
          }}
        />

        {/* Badge counter */}
        {showBadge && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 rounded-full text-[11px] font-black text-white flex items-center justify-center shadow-md animate-pulse"
            style={{
              backgroundColor: criticalCount > 0 ? '#EF4444' : '#F59E0B',
            }}
          >
            {alertCount}
          </span>
        )}
      </button>

      {/* Modern Budget Warnings Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-xl max-h-[85vh] flex flex-col p-0 overflow-hidden rounded-2xl"
          style={{
            backgroundColor: theme.cardBackground,
            borderColor: theme.divider,
            color: theme.text,
          }}
        >
          {/* Modal Header */}
          <div
            className="p-5 border-b flex items-center justify-between"
            style={{
              borderColor: theme.divider,
              backgroundColor: isDark ? '#111827' : '#F8FAFC',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  backgroundColor: criticalCount > 0 ? '#EF444420' : '#F59E0B20',
                  color: criticalCount > 0 ? '#EF4444' : '#F59E0B',
                }}
              >
                {criticalCount > 0 ? (
                  <AlertOctagon className="w-5 h-5" />
                ) : (
                  <AlertTriangle className="w-5 h-5" />
                )}
              </div>
              <div>
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <span>Budget & Financial Alerts</span>
                  {alertCount > 0 && (
                    <span
                      className="text-xs font-black px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: criticalCount > 0 ? '#EF444420' : '#F59E0B20',
                        color: criticalCount > 0 ? '#EF4444' : '#F59E0B',
                      }}
                    >
                      {alertCount} Active
                    </span>
                  )}
                </DialogTitle>
                <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
                  Day {budgetData?.currentDay} of month • Predictive burn-rate & pace analysis
                </p>
              </div>
            </div>

            {alertCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setIsDismissed(true);
                  setIsOpen(false);
                }}
                className="text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                style={{ borderColor: theme.divider, color: theme.textSecondary }}
              >
                Mark as Reviewed
              </button>
            )}
          </div>

          {/* Quick Metrics Strip */}
          <div
            className="px-5 py-3 border-b flex items-center justify-between text-xs"
            style={{
              borderColor: theme.divider,
              backgroundColor: isDark ? '#1F2937' : '#F1F5F9',
            }}
          >
            <div>
              <span style={{ color: theme.textSecondary }}>Overall Monthly Spend: </span>
              <strong style={{ color: theme.text }}>
                {formatCurrency(budgetData?.totalSpent || 0)}
              </strong>
              <span className="text-gray-400"> / {formatCurrency(budgetData?.totalBudget || 0)}</span>
            </div>
            <div className="flex items-center gap-3">
              {criticalCount > 0 && (
                <span className="font-bold text-red-500">
                  ● {criticalCount} Over Budget
                </span>
              )}
              {warningCount > 0 && (
                <span className="font-bold text-amber-500">
                  ● {warningCount} Near Limit
                </span>
              )}
            </div>
          </div>

          {/* Alerts Scrollable Body */}
          <div className="p-5 overflow-y-auto space-y-3.5 flex-1">
            {alertCount === 0 ? (
              <div className="py-12 text-center">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center bg-emerald-500/15 text-emerald-500">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-base font-bold" style={{ color: theme.text }}>
                  All Budgets are Healthy!
                </h4>
                <p className="text-xs mt-1 max-w-xs mx-auto" style={{ color: theme.textSecondary }}>
                  No spending anomalies detected. All categories are well within their scheduled daily limits.
                </p>
              </div>
            ) : (
              activeAlerts.map((item) => {
                const isOver = item.status === BUDGET_STATUS.OVERBUDGET;
                const cardAccent = isOver ? '#EF4444' : '#F59E0B';

                return (
                  <div
                    key={item.category}
                    className="p-4 rounded-xl border transition-all"
                    style={{
                      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                      borderColor: isDark ? '#374151' : '#E2E8F0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                  >
                    {/* Top Row: Category + Spent/Budget */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm" style={{ color: theme.text }}>
                          {item.category}
                        </span>
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: `${cardAccent}20`,
                            color: cardAccent,
                          }}
                        >
                          {item.statusTitle}
                        </span>
                      </div>

                      <div className="text-right">
                        <span className="text-sm font-bold" style={{ color: cardAccent }}>
                          {formatCurrency(item.spent)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {' '}/ {formatCurrency(item.budgetLimit)}
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div
                      className="w-full h-2 rounded-full overflow-hidden mb-2.5"
                      style={{ backgroundColor: isDark ? '#374151' : '#E2E8F0' }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(item.percentage, 100)}%`,
                          backgroundColor: cardAccent,
                        }}
                      />
                    </div>

                    {/* Burn rate + Projection + Actionable Advice in selected currency */}
                    <div
                      className="p-2.5 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs"
                      style={{
                        backgroundColor: isDark ? '#111827' : '#F8FAFC',
                        border: `1px solid ${theme.divider}`,
                      }}
                    >
                      <div className="flex items-center gap-1.5">
                        <Flame className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                        <span style={{ color: theme.textSecondary }}>
                          Daily Burn: <strong style={{ color: theme.text }}>{formatCurrency(item.dailyBurnRate)}/day</strong>
                        </span>
                        <span className="text-gray-400">•</span>
                        <span style={{ color: theme.textSecondary }}>
                          Est: <strong style={{ color: cardAccent }}>{formatCurrency(item.projectedTotal)}</strong>
                        </span>
                      </div>

                      <div className="font-semibold text-right" style={{ color: cardAccent }}>
                        {item.advice}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Modal Footer */}
          <div
            className="p-4 border-t flex items-center justify-between"
            style={{
              borderColor: theme.divider,
              backgroundColor: isDark ? '#111827' : '#F8FAFC',
            }}
          >
            <span className="text-xs" style={{ color: theme.textSecondary }}>
              Tip: You can adjust category limits anytime in Settings or Stats.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
              style={{ borderColor: theme.divider, color: theme.text }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
